/**
 * Onboarding · Step 6 — Three images are landing.
 *
 * The frontend `seedOnboardingArtifacts` pipeline (kicked off in Step 5)
 * is fetching and uploading the three product images. This step watches
 * the canvas nodes mirror and `useCanvasFileRecords` for
 * newly-added image-typed file_record nodes that appeared after mount.
 *
 * Detection: image-type file_record node = `entity.base === "file_record"` +
 * `content_type.startsWith("image/")`. Falls back to the API cache via
 * `useCanvasFileRecords` if the entity doesn't carry `content_type` inline
 * (agent-placed records only stamp `id` + `title` on the entity; the full
 * record lives in the file-records API cache).
 *
 * Once three image nodes land:
 *  - Persists their ids as `data.imageNodeIds` in Loro.
 *  - Swaps the coachmark copy from "Sketching three concepts" → "three product shots".
 *  - Auto-advances after ~1.5s.
 *
 * If more than three images appear (e.g. user manually added one), only the
 * first three matching the AeroCarry seed names are captured; otherwise we
 * take the first three new image nodes since mount.
 */

import { useCanvasFileRecords } from "@/api/fileRecords";
import type { FileRecord } from "@cohesive/api-client";
import type { Node } from "@xyflow/react";
import { memo, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useCanvasApi } from "../../../../context/CanvasContext";
import { useCanvasNodesMirror } from "../../../../context/CanvasNodesMirrorContext";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../../../../context/CanvasRouteContext";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const TARGET_IMAGE_COUNT = 3;
const POST_RESOLVE_READ_MS = 1500;

/**
 * Determine whether a canvas node is an image-type file_record. Uses
 * the entity's inline `content_type` first; falls back to looking the
 * record up in the API cache if that field is absent (agent-placed nodes
 * often only stamp `id` + `title` without the full record shape).
 */
function isImageEntityLikeNode(
  node: Node,
  fileRecordMap: Map<string, FileRecord>,
): boolean {
  if (node.type !== "entity") return false;
  const entity = (
    node.data as {
      entity?: { base?: string; data?: { content_type?: string; id?: string } };
    }
  )?.entity;
  if (!entity) return false;
  if (entity.base !== "file_record") return false;

  // Inline content_type (user-uploaded path)
  const inline = entity.data?.content_type;
  if (inline) return inline.startsWith("image/");

  // API cache fallback (agent-placed path)
  const recordId = entity.data?.id ?? node.id;
  const record = recordId ? fileRecordMap.get(recordId) : undefined;
  if (record?.content_type) return record.content_type.startsWith("image/");

  return false;
}

export const ImagesWorkingStep = memo(function ImagesWorkingStep({
  id,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const rawNodes = useCanvasNodesMirror(canvasId);
  const { data: fileRecords = [] } = useCanvasFileRecords(
    canvasId || undefined,
  );

  // Build a map for fast record lookup.
  const fileRecordMap = useMemo(() => {
    const m = new Map<string, FileRecord>();
    for (const r of fileRecords) m.set(r.id, r);
    return m;
  }, [fileRecords]);

  // Seal the baseline node set at mount so only nodes added AFTER this
  // step started count toward the target.
  const baselineIdsRef = useRef<Set<string> | null>(null);
  if (baselineIdsRef.current === null) {
    baselineIdsRef.current = new Set(rawNodes.map((n) => n.id));
  }

  // Count new image-type nodes that arrived after mount.
  const newImageNodes = useMemo(() => {
    const baseline = baselineIdsRef.current ?? new Set<string>();
    return rawNodes.filter(
      (n) => !baseline.has(n.id) && isImageEntityLikeNode(n, fileRecordMap),
    );
  }, [rawNodes, fileRecordMap]);

  const resolvedCount = Math.min(newImageNodes.length, TARGET_IMAGE_COUNT);
  const allResolved = resolvedCount >= TARGET_IMAGE_COUNT;

  // Keep a ref to the latest image nodes so the advance effect can read
  // their ids without listing `newImageNodes` as a dep — that array is a
  // new reference every render, which would cancel the advance timeout
  // mid-flight via the effect cleanup each time the node list updates.
  const newImageNodesRef = useRef(newImageNodes);
  newImageNodesRef.current = newImageNodes;

  // Unlock viewport so user can pan to see the images arriving.
  useEffect(() => {
    update({ lockedViewport: false, highlightTargetId: null });
    return () => {
      update({ lockedViewport: false });
    };
  }, [update]);

  // Once all three resolve: persist their ids, then auto-advance.
  // Only `allResolved` (boolean) is in deps — stable once true, so the
  // cleanup never cancels the timeout after it has been created.
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!allResolved) return;
    if (advancedRef.current) return;
    advancedRef.current = true;

    const imageIds = newImageNodesRef.current
      .slice(0, TARGET_IMAGE_COUNT)
      .map((n) => n.id);
    canvas.updateNodeData(id, { imageNodeIds: imageIds });

    const t = window.setTimeout(
      () => onAdvanceRef.current(),
      POST_RESOLVE_READ_MS,
    );
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allResolved]);

  return (
    <div
      style={
        {
          "--co-onb-stack-md": "14px",
          "--co-onb-pad-x": "22px",
          "--co-onb-pad-y": "20px",
        } as CSSProperties
      }
    >
      {/* The card stays in its "working" state through to the advance —
          no separate resolved screen. The former resolved variant only
          flashed for a moment before the step progressed, which read as
          a glitch. */}
      <StepHeader
        heading={
          <>
            Creating product <Flourish>shots</Flourish>.
          </>
        }
        lede={
          <>The agent is generating three product images based on the brief.</>
        }
      />

      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily:
            "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
          fontSize: "var(--co-onb-fs-hint)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--co-fg-muted)",
          transition: "color 320ms ease",
        }}
      >
        <span
          className="_co_onb_dot"
          style={{
            width: "var(--co-onb-dot)",
            height: "var(--co-onb-dot)",
            borderRadius: "50%",
            background: "var(--co-rust)",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {`WORKING… ${resolvedCount} of ${TARGET_IMAGE_COUNT}`}
      </div>
    </div>
  );
});
